import React, { useState } from "react";
import { injectIntl } from "react-intl";
import cx from 'classnames';
import withStyles from "isomorphic-style-loader/withStyles";
import { useMutation } from "@apollo/client";
import { Container } from "react-bootstrap";

import PlaceGeoSuggest from "../../components/Common/PlaceGeoSuggest/PlaceGeoSuggest";
import Loader from "../../components/Common/Loader/Loader";
import useQueryResponse from "../../lib/hooks/useQueryResponse";
import showToaster from "../../helpers/toastrMessgaes/showToaster";
import { getAllSavedLocations, addSavedLocations, removeSavedLocations } from "../../lib/graphql";
import messages from "../../locale/messages";
import bt from '../../components/commonStyle.css';
import s from "./SavedAddresses.css";

const emptyForm = {
    id: null,
    location: '',
    lat: null,
    lng: null,
    locationType: 'home',
    locationName: ''
};

const SavedAddresses = (props) => {
    const { intl: { formatMessage } } = props;

    const { data, loading, refetch } = useQueryResponse({
        query: getAllSavedLocations,
        ssr: true,
        fetchPolicy: 'network-only'
    });

    const [addLocation] = useMutation(addSavedLocations);
    const [removeLocation] = useMutation(removeSavedLocations);

    const [form, setForm] = useState(emptyForm);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);

    const results = data?.getAllSavedLocations?.results || [];

    const typeOptions = [
        { value: 'home', label: formatMessage(messages.homeLabel) },
        { value: 'work', label: formatMessage(messages.workLabel) },
        { value: 'other', label: formatMessage(messages.otherLabel) }
    ];

    const handleSelectSuggest = (place, value) => {
        setForm((prev) => ({
            ...prev,
            location: value,
            lat: place?.location?.latitude,
            lng: place?.location?.longitude
        }));
    };

    const openAdd = () => {
        setForm(emptyForm);
        setShowForm(true);
    };

    const openEdit = (item) => {
        setForm({
            id: item?.id,
            location: item?.location,
            lat: item?.lat,
            lng: item?.lng,
            locationType: item?.locationType || 'home',
            locationName: item?.locationName || ''
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setForm(emptyForm);
        setShowForm(false);
    };

    const handleSave = async () => {
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            showToaster({ messageId: 'networkFailed', toasterType: 'error' });
            return;
        }
        if (!form?.location || form?.lat == null || form?.lng == null) {
            showToaster({ messageId: 'errorMessage', requestContent: formatMessage(messages.selectAddressWarning), toasterType: 'error' });
            return;
        }
        setSaving(true);
        try {
            const { data: resp } = await addLocation({
                variables: {
                    id: form?.id ? Number(form.id) : undefined,
                    location: form.location,
                    lat: parseFloat(form.lat),
                    lng: parseFloat(form.lng),
                    locationType: form.locationType,
                    locationName: form?.locationName ? form.locationName.trim() : null
                }
            });
            setSaving(false);
            if (resp?.addSavedLocations?.status === 200) {
                showToaster({ messageId: 'errorMessage', requestContent: formatMessage(form?.id ? messages.addressUpdated : messages.addressAdded), toasterType: 'success' });
                closeForm();
                await refetch();
            } else {
                showToaster({ messageId: 'errorMessage', requestContent: resp?.addSavedLocations?.errorMessage, toasterType: 'error' });
            }
        } catch (error) {
            setSaving(false);
            showToaster({ messageId: 'errorMessage', requestContent: error?.message, toasterType: 'error' });
        }
    };

    const handleDelete = async (item) => {
        if (typeof window !== 'undefined' && !window.confirm(formatMessage(messages.deleteAddressConfirm))) {
            return;
        }
        try {
            const { data: resp } = await removeLocation({ variables: { id: Number(item?.id) } });
            if (resp?.removeSavedLocations?.status === 200) {
                showToaster({ messageId: 'errorMessage', requestContent: formatMessage(messages.addressDeleted), toasterType: 'success' });
                await refetch();
            } else {
                showToaster({ messageId: 'errorMessage', requestContent: resp?.removeSavedLocations?.errorMessage, toasterType: 'error' });
            }
        } catch (error) {
            showToaster({ messageId: 'errorMessage', requestContent: error?.message, toasterType: 'error' });
        }
    };

    const typeLabel = (type) => {
        const found = typeOptions.find((o) => o.value === type);
        return found ? found.label : formatMessage(messages.otherLabel);
    };

    return (
        <div className={cx('subPageContainer')}>
            <Container>
                <div className='subPageWrapper'>
                    <div className={s.headerRow}>
                        <p className={s.pageTitle}>{formatMessage(messages.savedAddresses)}</p>
                        {!showForm && (
                            <button type="button" className={cx(bt.btnPrimary, s.addBtn)} onClick={openAdd}>
                                {formatMessage(messages.addAddress)}
                            </button>
                        )}
                    </div>

                    {showForm && (
                        <div className={s.formCard}>
                            <p className={s.formTitle}>{form?.id ? formatMessage(messages.editAddress) : formatMessage(messages.addAddress)}</p>

                            <div className={s.fieldGroup}>
                                <label className={bt.commonLabelText}>{formatMessage(messages.addressLabel)}</label>
                                <PlaceGeoSuggest
                                    id="savedAddressGeoSuggest"
                                    initialValue={form?.location || ''}
                                    label={formatMessage(messages.addressLabel)}
                                    handleSelectSuggest={(place, value) => handleSelectSuggest(place, value)}
                                    onChange={(value) => setForm((prev) => ({ ...prev, location: value || '' }))}
                                    inputClass={cx(bt.formControlInput, 'form-control')}
                                    containerClassName="categoryLocationSearchContainer"
                                />
                            </div>

                            <div className={s.fieldGroup}>
                                <label className={bt.commonLabelText}>{formatMessage(messages.addressTypeLabel)}</label>
                                <div className={s.typeSelector}>
                                    {typeOptions.map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            className={cx(s.typePill, { [s.typePillActive]: form?.locationType === opt.value })}
                                            onClick={() => setForm((prev) => ({ ...prev, locationType: opt.value }))}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className={s.fieldGroup}>
                                <label className={bt.commonLabelText}>{formatMessage(messages.addressNameLabel)}</label>
                                <input
                                    type="text"
                                    className={cx(bt.formControlInput, 'form-control')}
                                    value={form?.locationName || ''}
                                    maxLength={50}
                                    placeholder={formatMessage(messages.addressNamePlaceholder)}
                                    onChange={(e) => setForm((prev) => ({ ...prev, locationName: e.target.value }))}
                                />
                            </div>

                            <div className={s.formBtnGroup}>
                                <button type="button" className={cx(bt.btnSecondary, s.cancelBtn)} onClick={closeForm}>
                                    {formatMessage(messages.cancelLabel)}
                                </button>
                                <Loader
                                    type={"button"}
                                    buttonType={"button"}
                                    show={saving}
                                    label={formatMessage(messages.saveAddress)}
                                    className={cx(bt.btnPrimary, 'loaderButtonFlex')}
                                    handleClick={handleSave}
                                />
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <p className={s.emptyText}>{formatMessage(messages.loadingText)}</p>
                    ) : results.length > 0 ? (
                        <ul className={s.addressList}>
                            {results.map((item) => (
                                <li key={item?.id} className={s.addressItem}>
                                    <div className={s.addressInfo}>
                                        <span className={s.addressType}>{typeLabel(item?.locationType)}</span>
                                        {item?.locationName ? <span className={s.addressName}>{item?.locationName}</span> : null}
                                        <span className={s.addressText}>{item?.location}</span>
                                    </div>
                                    <div className={s.addressActions}>
                                        <button type="button" className={s.linkBtn} onClick={() => openEdit(item)}>
                                            {formatMessage(messages.editAddress)}
                                        </button>
                                        <button type="button" className={cx(s.linkBtn, s.deleteLink)} onClick={() => handleDelete(item)}>
                                            {formatMessage(messages.deleteAddress)}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className={s.emptyText}>{formatMessage(messages.noSavedAddresses)}</p>
                    )}
                </div>
            </Container>
        </div>
    );
};

export default injectIntl(withStyles(bt, s)(SavedAddresses));
